import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPhone, FiMenu, FiClock, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import MobileDrawer from './MobileDrawer';
import { useData } from '../contexts/DataContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const location = useLocation();

  // Get data from global context
  const { therapies, settings } = useData();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMegaMenuOpen(false);
    setIsDrawerOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm transition-all border-b border-gray-100 mb-0`}>
        {/* Top Announcement Bar */}


        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo - Updated to use Image */}
            <Link to="/" className="flex items-center group flex-shrink-0">
              <img 
                src={settings.logo_light || "/backend/uploads/logo_light_b6c9d5a783f8f947.png"} 
                alt="Jiva Nadi Logo" 
                className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              {/* Fallback Text if image fails to load */}
              <div className="hidden ml-2">
                <div className="text-xl lg:text-2xl font-black text-emerald-900 tracking-tighter">
                  JIVA NADI
                </div>
                <div className="text-[9px] lg:text-[10px] font-bold text-emerald-600 uppercase tracking-[0.2em] -mt-1">
                  Therapy & Chiropractor
                </div>
              </div>
            </Link>

            {/* Main Navigation */}
            <nav className="hidden xl:flex items-center space-x-1">
              <Link to="/" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Home</Link>
              <Link to="/about" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">About Us</Link>
              
              {/* Therapy Mega Menu - Clean 4x4 Grid */}
              <div 
                className="relative group"
                onMouseEnter={() => setIsMegaMenuOpen(true)}
                onMouseLeave={() => setIsMegaMenuOpen(false)}
              >
                <button className="flex items-center px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">
                  Therapy <SafeIcon icon={FiChevronDown} className="ml-1 w-4 h-4" />
                </button>
                
                <div className={`fixed top-19 left-0 right-0 w-full bg-white shadow-2xl border-b border-emerald-100 transition-all duration-300 transform z-40 ${isMegaMenuOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'}`}>
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600"></div>
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                      {therapies.map((item) => (
                        <Link 
                          key={item.id} 
                          to={item.path}
                          className="group/item flex items-stretch rounded-xl border border-gray-100 hover:border-emerald-300 bg-white hover:bg-gradient-to-br hover:from-emerald-50 hover:to-white shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                        >
                          {/* Left Image - Full Height No Padding */}
                          <div className="flex-shrink-0 w-16 h-auto mr-2.5 overflow-hidden bg-gray-100">
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=200&h=200&fit=crop';
                              }}
                            />
                          </div>
                          
                          {/* Right Text - Vertically Centered */}
                          <div className="flex-1 flex flex-col justify-center mr-2.5 min-w-0">
                            <h3 className="font-bold text-gray-800 text-xs sm:text-sm group-hover/item:text-emerald-700 transition-colors line-clamp-1 leading-tight">
                              {item.title}
                            </h3>
                            <p className="text-gray-600 text-[10px] sm:text-xs line-clamp-1 mt-0.5 opacity-80">
                              {item.tagline || item.description}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <Link to="/services" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Services</Link>
              <Link to="/courses" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Courses</Link>
              <Link to="/gallery" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Gallery</Link>
              <Link to="/videos" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Videos</Link>
              <Link to="/testimonials" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Testimonials</Link>
              <Link to="/faq" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">FAQ</Link>
              <Link to="/blog" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Blog</Link>
              <Link to="/contact" className="px-3 py-2 text-gray-700 hover:text-emerald-700 font-semibold text-sm rounded-lg hover:bg-emerald-50 transition-colors">Contact Us</Link>
            </nav>

            {/* CTA */}
            <div className="flex items-center space-x-4">
              <a href={`tel:${settings.contact_phone || settings.phone1 || '9581556660'}`} className="hidden lg:flex items-center space-x-2 text-emerald-800 font-bold text-sm bg-emerald-50 px-4 py-2 rounded-full border border-emerald-100 hover:bg-emerald-100 transition-colors">
                <SafeIcon icon={FiPhone} className="text-emerald-600" />
                <span>{settings.contact_phone || settings.phone1 || '9581556660'}</span>
              </a>
              <button 
                onClick={() => setIsDrawerOpen(true)}
                className="xl:hidden p-2 text-emerald-900 rounded-lg hover:bg-emerald-50"
              >
                <SafeIcon icon={FiMenu} className="w-7 h-7" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  );
};

export default Header;